import { Fragment, memo } from 'react';

import PropTypes from 'prop-types';
import classnames from 'classnames';

import { UnderlinedButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import { FILTER_REMOVED } from '../../analytics/analyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import StatefulLink, { sortFilter } from '../../stateful-link/StatefulLink';

import styles from './ClearFilters.module.scss';
import { useClearFiltersData } from './data/useClearFiltersData';

const ClearFilters = ({
  baseFacetId = '',
  hasLargeButton = false,
  isHorizontalFilters = false,
  isInStickyLHN = false,
  mobileFilterDrawer = false,
  onClearAllClick = undefined,
  query,
  show = false,
  showCount = false,
}) => {
  const { facetContext, router, analyticsData } = useClearFiltersData();
  const {
    applied: { filterCombination, filterCombinationNvals },
  } = facetContext;

  const { type } = router.query;

  const rootFacetId = ['brand', 'browse'].includes(type) ? baseFacetId : '';

  const { pageType } = analyticsData;

  const appliedFiltersCount = filterCombination?.split('|').length;
  const defaultText =
    appliedFiltersCount === 1 ? 'Clear filter' : 'Clear all filters';
  const horizontalFiltersText = 'Clear all';
  const mobileFilterDrawerText = 'Clear filters';
  const countText = `${defaultText} (${appliedFiltersCount})`;

  const filterRemovedEvent = useAnalytics(FILTER_REMOVED, {
    pagetype: pageType,
    appliedFilter: filterCombination,
    appliedFilterNval: filterCombinationNvals,
    filterCombination: '',
    filterCombinationNvals: '',
  });

  const triggerEvents = () => {
    filterRemovedEvent();
    if (onClearAllClick) {
      onClearAllClick();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <Fragment>
      <StatefulLink
        className={
          mobileFilterDrawer
            ? styles.clearAllButton
            : classnames(styles.link, {
                [styles['underline-offset']]: !mobileFilterDrawer,
                [styles['sticky-lhn']]: isInStickyLHN,
              })
        }
        facetId={rootFacetId}
        onClick={() => triggerEvents()}
        query={{ ...query }}
        queryOpts={{ filter: sortFilter() }}
      >
        <UnderlinedButton
          className={classnames(styles.clearAllDrawerButton, {
            [styles['clearAllDrawerButton--large']]: hasLargeButton,
          })}
          size={hasLargeButton ? '' : 'small'}
          data-testid="clear-filter-button"
        >
          {mobileFilterDrawer
            ? mobileFilterDrawerText
            : isHorizontalFilters
              ? horizontalFiltersText
              : showCount
                ? countText
                : defaultText}
        </UnderlinedButton>
      </StatefulLink>
    </Fragment>
  );
};

ClearFilters.propTypes = {
  baseFacetId: PropTypes.string,
  hasLargeButton: PropTypes.bool,
  isInStickyLHN: PropTypes.bool,
  mobileFilterDrawer: PropTypes.bool,
  query: PropTypes.object,
  show: PropTypes.bool,
  showCount: PropTypes.bool,
};

export default memo(ClearFilters);
